/**
 * Normalization Layer - Single Source of Truth
 * 
 * This module creates a normalized output structure that serves as the
 * single source of truth for Story UI, charts, and export.
 * 
 * Key Goals:
 * 1. Create a "currentOption" object that represents baseline/do-nothing
 * 2. Produce a stable "optionsToDisplay" list (recommended + best alternatives)
 * 3. Build "chartSeries" with deduplicated options only
 * 4. Sanitize all numeric values (no NaN/Infinity)
 * 5. Track missing/invalid values in an "issues" array
 */

import type { SystemRecInput, SystemRecOutput, RecommendationOption } from './types';
import type { BaselineResult } from './baseline';
import { finiteOrNull, checkValue } from './sanitize';

// ==================== TYPES ====================

export interface NormalizedOption {
  id: string;
  title: string;
  displayName: string;

  // Core metrics (sanitized, null if invalid)
  annualCost: number | null;
  totalCost: number | null;
  upfrontCost: number | null;

  // Carbon
  annualCO2_kg: number | null;
  totalCO2_kg: number | null;

  // Lifespan
  lifespanYears: { min: number; max: number } | null;
  replacementsInPeriod: number | null;

  // Delta vs baseline (null for baseline itself)
  deltaAnnualCost: number | null;
  deltaTotalCost: number | null;
  deltaAnnualCO2: number | null;

  // Original option data (for reference)
  originalOption?: RecommendationOption;

  // Is this the current/baseline option?
  isCurrent?: boolean;

  // Confidence and labeling
  confidence?: 'high' | 'medium' | 'low';
  isEstimated?: boolean;  // True if confidence is LOW
}

export interface NormalizedBaseline {
  source: string;
  efficiency: number | null;
  
  annualCost: number | null;
  totalCost: number | null;
  periodYears: number;
  
  lifespanYears: { min: number; max: number } | null;
  remainingLifespan: number | null;
  replacementsInPeriod: number | null;
  
  annualCO2_kg: number | null;
  totalCO2_kg: number | null;
  
  // Issues detected in baseline calculation
  issues: string[];
}

export interface NormalizedOutput {
  // Core normalized data
  baseline: NormalizedBaseline;
  currentOption: NormalizedOption;  // Represents "do nothing" - same data as baseline
  optionsToDisplay: NormalizedOption[];  // Primary + up to 2 best alternatives
  chartSeries: NormalizedOption[];  // [currentOption, ...optionsToDisplay] for charts
  
  // Issues across all data
  issues: string[];
  
  // Original output (for reference)
  rawOutput: SystemRecOutput;
  rawInput: SystemRecInput;
}

// ==================== NORMALIZATION ====================

/**
 * Normalize the raw system recommendation output into a clean, sanitized structure
 */
export function normalizeSystemRecOutput(
  rawOutput: SystemRecOutput,
  input: SystemRecInput
): NormalizedOutput {
  const issues: string[] = [];
  
  // Step 1: Normalize baseline
  const baseline = normalizeBaseline(rawOutput.baselineResult, issues);
  
  // Step 2: Create currentOption from baseline
  const currentOption = createCurrentOption(baseline, rawOutput.baselineResult, issues);
  
  // Step 3: Normalize all options (primary + alternatives)
  const allNormalizedOptions = normalizeOptions(rawOutput, baseline, issues);
  
  // Step 4: Select options to display (primary + up to 2 best alternatives)
  const optionsToDisplay = selectOptionsToDisplay(allNormalizedOptions, 3);
  
  // Step 5: Build chart series (current + displayed options, deduplicated)
  const chartSeries = buildChartSeries(currentOption, optionsToDisplay);
  
  return {
    baseline,
    currentOption,
    optionsToDisplay,
    chartSeries,
    issues,
    rawOutput,
    rawInput: input,
  };
}

/**
 * Normalize baseline result with sanitization
 */
function normalizeBaseline(
  baselineResult: BaselineResult | undefined,
  issues: string[]
): NormalizedBaseline {
  if (!baselineResult) {
    issues.push('Baseline: No baseline data available - additional system information needed');
    return {
      source: 'Unknown',
      efficiency: null,
      annualCost: null,
      totalCost: null,
      periodYears: 20,
      lifespanYears: null,
      remainingLifespan: null,
      replacementsInPeriod: null,
      annualCO2_kg: null,
      totalCO2_kg: null,
      issues: ['No baseline data available'],
    };
  }
  
  const baselineIssues: string[] = [];
  
  // Sanitize numeric values
  const efficiency = finiteOrNull(baselineResult.initialEfficiency);
  const annualCost = finiteOrNull(baselineResult.annualizedCost_gbp);
  const totalCost = finiteOrNull(baselineResult.totalCost_gbp);
  const remainingLifespan = finiteOrNull(baselineResult.longevity?.remainingLifespan_years);
  const replacementsInPeriod = finiteOrNull(baselineResult.longevity?.expectedReplacementsInPeriod);
  
  // Check for missing values
  const efficiencyIssue = checkValue(efficiency, 'Baseline efficiency', 'Could not determine current system efficiency');
  if (efficiencyIssue) baselineIssues.push(efficiencyIssue);
  
  const costIssue = checkValue(annualCost, 'Baseline annual cost', 'Not enough information to calculate cost');
  if (costIssue) {
    baselineIssues.push(costIssue);
    issues.push(costIssue);
  }
  
  // Extract lifespan data
  let lifespanYears: { min: number; max: number } | null = null;
  if (baselineResult.longevity?.expectedLifespanYears) {
    const expected = baselineResult.longevity.expectedLifespanYears;
    lifespanYears = {
      min: finiteOrNull(expected.min) || 0,
      max: finiteOrNull(expected.max) || 0,
    };
  }
  
  return {
    source: baselineResult.efficiencySource || baselineResult.source || 'Unknown',
    efficiency,
    annualCost,
    totalCost,
    periodYears: baselineResult.periodYears || 20,
    lifespanYears,
    remainingLifespan,
    replacementsInPeriod,
    annualCO2_kg: null, // TODO: Add carbon data when available
    totalCO2_kg: null,
    issues: baselineIssues,
  };
}

/**
 * Create a "currentOption" object that represents "do nothing" baseline
 * This ensures baseline and options have the same structure
 */
function createCurrentOption(
  baseline: NormalizedBaseline,
  baselineResult: BaselineResult | undefined,
  issues: string[]
): NormalizedOption {
  return {
    id: 'current',
    title: 'Do Nothing (Keep Current System)',
    displayName: 'Current System',
    
    annualCost: baseline.annualCost,
    totalCost: baseline.totalCost,
    upfrontCost: 0, // No upfront cost for keeping current system
    
    annualCO2_kg: baseline.annualCO2_kg,
    totalCO2_kg: baseline.totalCO2_kg,
    
    lifespanYears: baseline.lifespanYears,
    replacementsInPeriod: baseline.replacementsInPeriod,
    
    // No delta for current system
    deltaAnnualCost: null,
    deltaTotalCost: null,
    deltaAnnualCO2: null,
    
    isCurrent: true,
  };
}

/**
 * Normalize all recommendation options
 * Uses year1 data when available for accurate cost/carbon values
 */
function normalizeOptions(
  rawOutput: SystemRecOutput,
  baseline: NormalizedBaseline,
  issues: string[]
): NormalizedOption[] {
  const allOptions: RecommendationOption[] = [
    rawOutput.primary,
    ...(rawOutput.alternatives || []),
  ].filter(Boolean);

  // Build a map of year1 data by option ID for quick lookup
  const year1DataMap = new Map<string, { costGbp: number; carbonKg: number; confidence?: string }>();
  if (rawOutput.year1?.options) {
    for (const optData of rawOutput.year1.options) {
      if (optData.impact) {
        year1DataMap.set(optData.optionId, {
          costGbp: optData.impact.costGbp,
          carbonKg: optData.impact.carbonKg,
          confidence: optData.impact.performance?.confidence,
        });
      }
    }
  }

  return allOptions.map(option => {
    const year1Data = year1DataMap.get(option.id);
    return normalizeOption(option, baseline, issues, year1Data);
  });
}

/**
 * Normalize a single option with sanitization
 * Prioritizes year1 data over legacy lcoo fields
 */
function normalizeOption(
  option: RecommendationOption,
  baseline: NormalizedBaseline,
  issues: string[],
  year1OptionData?: { costGbp: number; carbonKg: number; confidence?: string }
): NormalizedOption {
  // Extract annual cost - prefer year1 data over legacy lcoo fields
  let annualCost: number | null = null;
  let confidence: 'high' | 'medium' | 'low' | undefined;

  // Priority 1: year1 data (computed in year1Impact.ts)
  if (year1OptionData?.costGbp !== undefined) {
    annualCost = finiteOrNull(year1OptionData.costGbp);
    confidence = (year1OptionData.confidence as 'high' | 'medium' | 'low') || 'medium';
  }

  // Priority 2: deltaVsBaseline computed value
  if (annualCost === null) {
    annualCost = finiteOrNull(option.deltaVsBaseline?.optionAnnualized_gbp);
  }

  // Priority 3: legacy lcoo fields (fallback)
  if (annualCost === null) {
    annualCost = finiteOrNull(
      option.lcoo?.annualizedCostGBP ||
      option.lcoo?.annualRunningGBP
    );
  }

  const totalCost = finiteOrNull(
    option.lcoo?.totalCost_gbp ||
    option.lcoo?.totalCostGBP
  );

  const upfrontCost = finiteOrNull(
    option.lcoo?.upfrontNetGBP ||
    option.lcoo?.upfrontCost_gbp ||
    option.lcoo?.upfrontGrossGBP
  );

  // Extract and sanitize deltas
  const deltaAnnualCost = finiteOrNull(option.deltaVsBaseline?.annualSaving_gbp);
  const deltaTotalCost = finiteOrNull(option.deltaVsBaseline?.totalSaving_gbp);

  // Extract lifespan
  let lifespanYears: { min: number; max: number } | null = null;
  if (option.longevity?.expectedLifespanYears) {
    const lifespan = option.longevity.expectedLifespanYears;
    lifespanYears = {
      min: finiteOrNull(lifespan.min) || 0,
      max: finiteOrNull(lifespan.max) || 0,
    };
  }

  const replacementsInPeriod = finiteOrNull(option.longevity?.replacementsInPeriod);

  // Extract carbon from year1 data if available
  const annualCO2_kg = year1OptionData?.carbonKg !== undefined
    ? finiteOrNull(year1OptionData.carbonKg)
    : null;

  // Check for issues - but don't hide data, just note the issue
  if (annualCost === null) {
    issues.push(`${option.title}: Annual cost could not be calculated`);
  }

  // Determine if this is an estimate (low confidence)
  const isEstimated = confidence === 'low';

  return {
    id: option.id,
    title: option.title,
    displayName: option.boilerLabel || option.title,

    annualCost,
    totalCost,
    upfrontCost,

    annualCO2_kg,
    totalCO2_kg: null, // Computed from annualCO2_kg * period if needed

    lifespanYears,
    replacementsInPeriod,

    deltaAnnualCost,
    deltaTotalCost,
    deltaAnnualCO2: null,

    originalOption: option,
    confidence,
    isEstimated,
  };
}

/**
 * Select which options to display (primary + best alternatives)
 */
function selectOptionsToDisplay(
  allOptions: NormalizedOption[],
  maxCount: number = 3
): NormalizedOption[] {
  // Sort by total savings (descending)
  const sorted = [...allOptions].sort((a, b) => {
    const savingsA = a.deltaTotalCost || 0;
    const savingsB = b.deltaTotalCost || 0;
    return savingsB - savingsA; // Higher savings first
  });
  
  // Return top N options
  return sorted.slice(0, maxCount);
}

/**
 * Build chart series with deduplicated options
 * Returns [currentOption, ...optionsToDisplay]
 */
function buildChartSeries(
  currentOption: NormalizedOption,
  optionsToDisplay: NormalizedOption[]
): NormalizedOption[] {
  // Use a Map to deduplicate by ID
  const seriesMap = new Map<string, NormalizedOption>();
  
  // Add current option first
  seriesMap.set(currentOption.id, currentOption);
  
  // Add displayed options (deduplicates automatically)
  for (const option of optionsToDisplay) {
    if (!seriesMap.has(option.id)) {
      seriesMap.set(option.id, option);
    }
  }
  
  // Return as array (maintains insertion order)
  return Array.from(seriesMap.values());
}
